import React, { useCallback, useState } from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { FONT_FAMILY } from '@provider-portal/common/styles/themeMui';

// import parse from 'react-html-parser';
import { CircularInfoBar } from '../CircularInfoBar/CircularInfoBar';
import { CircularProgressBar } from '../CircularProgressBar/CircularProgressBar';
import { MetricExplanationPopup } from '../MetricExplanationPopup/MetricExplanationPopup';
import { MetricsCardStrings } from './EligibilityMetrics';

export enum StatProgressBarVariant {
  CIRCULAR_PROGRESS_BAR,
  CIRCULAR_INFO_BAR,
}

export interface EligibilityMetricCardProps {
  strings: MetricsCardStrings;
  metrics: {
    current: number | string;
    target: number | string;
    percentage: number;
    statProgressBarVariant: StatProgressBarVariant;
    haveStarInCircularInfoBar?: boolean;
  };
}

const styles = {
  metric: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '24px',
    gap: '24px',
    backgroundColor: '#F4F5F6',
    borderRadius: '16px',
    flex: '1 0 0',
    order: 0,
    flexGrow: 1,
  },
  metricTitle: {
    color: '#2F313F',
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: FONT_FAMILY,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
  },
  metricThresholdExplanation: {
    color: '#2F313F',
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: FONT_FAMILY,
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '20px',
  },
  button: {
    color: '#1D965C',
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: FONT_FAMILY,
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '20px',
    padding: '0px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
    textTransform: 'none',
  },
} as const;

export const EligibilityMetricCard = ({ metrics, strings }: EligibilityMetricCardProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <Box sx={styles.metric}>
        <Grid container direction="row" justifyContent="flex-end">
          <Grid item sx={{ flexGrow: 1 }}>
            <Typography sx={styles.metricTitle}>{strings.title}</Typography>
            <Typography sx={styles.metricThresholdExplanation}>
              {strings.thresholdExplanation}
            </Typography>
            <Button variant="text" onClick={handleOpen} sx={styles.button}>
              {strings.achievementApproachPopup}
            </Button>
          </Grid>
          <Grid item>
            {metrics.statProgressBarVariant === StatProgressBarVariant.CIRCULAR_PROGRESS_BAR && (
              <CircularProgressBar
                label={metrics.current as number}
                percentage={metrics.percentage}
              />
            )}
            {metrics.statProgressBarVariant === StatProgressBarVariant.CIRCULAR_INFO_BAR && (
              <CircularInfoBar
                label={metrics.current.toString()}
                percentage={metrics.percentage}
                haveStar={!!metrics.haveStarInCircularInfoBar}
              />
            )}
          </Grid>
        </Grid>
        <MetricExplanationPopup
          title={strings.title}
          htmlBody={strings.achievementApproachHtml}
          isPopupOpen={isOpen}
          handleClose={handleClose}
        />
      </Box>
    </>
  );
};
